/**
 * The how-to-play overlay — a single, scrollable reference reachable from the
 * home screen and from inside a game. Written to answer the questions new
 * players actually hit: how a turn works now that drawing is automatic, and the
 * royalty rules (why the King can't always be captured, what the Prince does).
 */
import { For, Show, onCleanup, onMount } from "solid-js";
import { DEFAULT_MAX_PLIES } from "../../core";
import { Icon } from "./Icon";

interface Section {
  readonly title: string;
  readonly body: readonly string[];
}

/** The hard turn cap, expressed as full moves (both players act = 2 plies). */
const MAX_TURNS = Math.floor(DEFAULT_MAX_PLIES / 2);

const SECTIONS: readonly Section[] = [
  {
    title: "The goal",
    body: [
      "Win by checkmating the enemy King — putting it under attack with no legal way out. The King is never actually captured off the board.",
    ],
  },
  {
    title: "Royalty: the King and the Prince",
    body: [
      "Each side starts with just a King and a Prince on the board; everything else you deploy from your deck.",
      "Both move one square in any direction, like a normal chess king. The King can capture an adjacent enemy piece — but it may never move into check, so it can't take a defended piece.",
      "While your Prince is alive, your King is completely immune: it cannot be checked or captured. So the first job is to hunt the enemy Prince. Once a side's Prince falls, that King is exposed and can be checkmated.",
      "Neither the King nor the Prince can be targeted by ability cards (no recalling or shoving royalty).",
    ],
  },
  {
    title: "Your turn",
    body: [
      "At the start of every turn you automatically draw a card (up to a hand limit of 7). Then you take exactly one action: deploy a card or move a piece.",
      "Drawing is automatic — it is not something you spend your turn on. The very first turn of the game is played from your dealt opening hand.",
    ],
  },
  {
    title: "Deploying",
    body: [
      "Play a piece card onto an empty square in your own two home ranks.",
      "Terrain (Mound, Hills, Walls) can be dropped on almost any empty square, not just your home ranks — it walls the board off and can never move or be captured.",
      "An Outpost projects a deployment node onto a nearby square each turn, letting you deploy a piece beyond your home ranks.",
    ],
  },
  {
    title: "Moving",
    body: [
      "Pieces move by standard chess rules. Pawns promote to a Queen on the far rank. There is no castling or en passant.",
      "Some fairy pieces are different: the Lance only slides straight forward, the Farmer is an inverse pawn, and the Captain gets one free move each turn that doesn't use your action.",
    ],
  },
  {
    title: "Check, checkmate & draws",
    body: [
      "You may never make a move (or a deploy) that leaves your own King in check. While in check, your action must get you out of it — move the King, capture the attacker, or deploy a piece to block.",
      "Checkmate wins. If the side to move has no legal action but is not in check, the game is a draw (stalemate). A game with no progress for a long stretch is also drawn.",
      `There is also a hard turn limit: if neither side has won after ${MAX_TURNS} full moves, the game ends in a draw. The turn counter in the top bar tracks it and warns you as the limit approaches.`,
      "Either player can offer a draw at any time with the Offer draw button. If your opponent accepts, the game ends in a draw; they can also decline and play on.",
    ],
  },
  {
    title: "Ability cards",
    body: [
      "Some cards are abilities, not pieces: Dash and Sidestep nudge a piece, Recall returns a piece to its owner's hand (the only way to clear terrain), and Resurrection brings one of your fallen pieces back.",
      "Playing an ability is your action for the turn.",
    ],
  },
  {
    title: "Clock (online)",
    body: [
      "Online games are played at 10 minutes each with a 5-second increment per move. If your clock hits zero, you lose on time.",
    ],
  },
];

export function RulesOverlay(props: { onClose: () => void }) {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") props.onClose();
  };
  onMount(() => window.addEventListener("keydown", onKey));
  onCleanup(() => window.removeEventListener("keydown", onKey));

  return (
    <div class="overlay" onClick={props.onClose}>
      <div class="overlay-card overlay-card--rules" onClick={(e) => e.stopPropagation()}>
        <div class="rules-head">
          <h2>How to play</h2>
          <button class="icon-link" title="Close" onClick={props.onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div class="rules-body">
          <For each={SECTIONS}>
            {(s) => (
              <section class="rules-section">
                <h3>{s.title}</h3>
                <For each={s.body}>{(p) => <p>{p}</p>}</For>
              </section>
            )}
          </For>
        </div>
        <Show when={true}>
          <div class="rules-foot">
            <button class="btn btn-primary" onClick={props.onClose}>
              Got it
            </button>
          </div>
        </Show>
      </div>
    </div>
  );
}
